<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Availability } from '@/types/booking-api';

import CartConfirmationModal from '@/components/booking/CartConfirmationModal.vue';
import PricingBreakdown from '@/components/booking/PricingBreakdown.vue';
import SessionCard from '@/components/booking/SessionCard.vue';
import { HOURS_IN_DAY, MS_PER_HOUR, SESSIONS_PAGE_SIZE } from '@/composables/booking/booking-widget.constants';
import { useBookingPricing } from '@/composables/booking/useBookingPricing';
import { ApiError } from '@/services/booking/cartsTransactionsApi';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useBookingWidgetContext } from '@/types/booking-widget-context';
import { pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';
import { convertOctoPrice, getLocalizedLabel } from '@/utils/utils';

const { t } = useI18n();
const { currentLocale, globalConfiguration } = storeToRefs(useGlobalConfigurationStore());
const context = useBookingWidgetContext();
const isLoadingAvailabilities = context.bookingWidget.isLoadingAvailabilities;
const isAddingToCart = context.bookingWidget.isAddingToCart;

const { pricingLines, getSessionPrice } = useBookingPricing(context);

const pricingUnits = computed(() => {
  return pricingLines.value.map(line => ({
    id: line.unitId,
    description: line.unitLabel,
    quantity: line.quantity,
    originalPricePerUnit: line.originalPricePerUnit,
    netPricePerUnit: line.pricePerUnit,
  }));
});

const pricingCurrency = computed(() => pricingLines.value[0]?.currency ?? 'EUR');
const pricingCurrencyPrecision = computed(() => pricingLines.value[0]?.currencyPrecision ?? 2);

const cartsTransactionsApi = context.cartsTransactionsApi;
const addToCartError = ref<string | null>(null);

const displayedSessionsCount = ref(SESSIONS_PAGE_SIZE);

const sortedSessions = computed(() => {
  const sessions = context.bookingWidget.availableSessions.value;
  const available = sessions.filter(s => s.available);
  const unavailable = sessions.filter(s => !s.available);
  return [...available, ...unavailable];
});

const visibleSessions = computed(() =>
  sortedSessions.value.slice(0, displayedSessionsCount.value),
);

const hasMoreSessions = computed(() =>
  displayedSessionsCount.value < context.bookingWidget.availableSessions.value.length,
);

watch(() => context.bookingWidget.availableSessions.value, () => {
  displayedSessionsCount.value = SESSIONS_PAGE_SIZE;
});

function showMoreSessions() {
  displayedSessionsCount.value += SESSIONS_PAGE_SIZE;
}

const optionTitle = computed(() => {
  const selectedOption = context.bookingWidget.selectedOption.value;
  if (!selectedOption)
    return '';
  return getLocalizedLabel(selectedOption.labels, selectedOption.label, context.bookingWidget.currentLocale.value);
});

const optionDuration = computed(() => {
  if (!context.bookingWidget.selectedOption.value)
    return null;

  const nextSessionByOption = context.activityDetails!.properties.find(
    prop => prop.propertyName === 'nextSessionByOption',
  );

  if (!nextSessionByOption)
    return null;

  const optionId = context.bookingWidget.selectedOption.value.id;
  const sessionData = nextSessionByOption.value[optionId];

  return sessionData?.duration ?? null;
});

const formattedDuration = computed(() => {
  if (optionDuration.value === null)
    return null;

  const totalMinutes = optionDuration.value;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${t('booking.durationHours', { hours }, hours)} ${t('booking.durationMinutes', { minutes }, minutes)}`;
  }
  if (hours > 0) {
    return t('booking.durationHours', { hours }, hours);
  }
  return t('booking.durationMinutes', { minutes }, minutes);
});

const formattedDate = computed(() => {
  if (!context.bookingWidget.selectedDate.value)
    return '';

  const date = context.bookingWidget.selectedDate.value;
  return new Intl.DateTimeFormat(currentLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
});

function selectSession(session: Availability) {
  if (!session.available)
    return;

  context.bookingWidget.handleSessionSelected(session);
}

function isSessionSelected(session: Availability): boolean {
  return context.bookingWidget.selectedSession.value?.id === session.id;
}

const hoursToBook = computed(() => {
  const selectedSession = context.bookingWidget.selectedSession.value;
  if (!selectedSession || !selectedSession.utcCutoffAt)
    return null;

  const cutoffDate = new Date(selectedSession.utcCutoffAt);
  const now = new Date();
  const diffMs = cutoffDate.getTime() - now.getTime();

  if (diffMs <= 0)
    return null;

  const hours = Math.ceil(diffMs / MS_PER_HOUR);

  if (hours >= HOURS_IN_DAY)
    return null;

  return hours;
});

const hasNoAvailableSessions = computed(() => {
  const sessions = context.bookingWidget.availableSessions.value;
  if (sessions.length === 0)
    return true;
  return sessions.every(session => !session.available);
});

const isCartModalOpen = ref(false);
const lastAddToCartSucceeded = ref(false);

async function handleAddToCartClick() {
  isAddingToCart.value = true;
  const selectedOption = context.bookingWidget.selectedOption.value;
  const selectedSession = context.bookingWidget.selectedSession.value;

  if (!selectedOption || !selectedSession) {
    console.error('Cannot add to cart: missing option or session');
    return;
  }

  addToCartError.value = null;

  try {
    const units = cartsTransactionsApi.buildUnitsFromPricingLines(pricingLines.value);

    const sessionLocalDate = selectedSession.localDateTimeStart.substring(0, 10);

    const paymentProductRef = `${context.activityDetails!.source}:${context.activityDetails!.sourceId}`;

    const response = await cartsTransactionsApi.addToCart(
      context.activityDetails!.id,
      paymentProductRef,
      selectedOption.id,
      selectedSession.id,
      sessionLocalDate,
      units,
      globalConfiguration.value!.distributorId,
    );

    if (response !== null) {
      const ga4Items = pricingLines.value
        .filter(line => line.quantity > 0)
        .map((line) => {
          const unitPrice = convertOctoPrice(line.pricePerUnit, line.currencyPrecision);
          const originalUnitPrice = convertOctoPrice(line.originalPricePerUnit, line.currencyPrecision);
          const discount = originalUnitPrice > unitPrice ? originalUnitPrice - unitPrice : undefined;

          return {
            item_id: context.activityDetails!.id,
            item_name: context.activityDetails!.name,
            price: unitPrice,
            quantity: line.quantity,
            currency: line.currency,
            item_variant: getLocalizedLabel(selectedOption.labels, selectedOption.label, currentLocale.value),
            item_brand: context.activityDetails!.source,
            discount,
          };
        });

      const totalValue = ga4Items.reduce((sum, item) => sum + item.price * item.quantity, 0);

      pushGA4EcommerceEvent('add_to_cart', {
        currency: pricingLines.value[0]?.currency ?? 'EUR',
        value: totalValue,
        items: ga4Items,
      });

      lastAddToCartSucceeded.value = true;
      isCartModalOpen.value = true;
    }
    else {
      addToCartError.value = 'Failed to add item to cart';
    }
  }
  catch (error) {
    console.error('Error adding to cart:', error);
    if (error instanceof ApiError && error.errorType === 'AvailabilityGoneException') {
      addToCartError.value = t('booking.addToCartErrorAvailabilityGone');
    }
    else {
      addToCartError.value = t('booking.addToCartErrorGeneric');
    }
  }
  finally {
    context.bookingWidget.isAddingToCart.value = false;
  }
}

function handleCartModalClose() {
  isCartModalOpen.value = false;
  if (lastAddToCartSucceeded.value) {
    lastAddToCartSucceeded.value = false;
    context.bookingWidget.resetAfterCartAdd();
  }
}

function handleCheckout() {
  handleCartModalClose();

  const config = globalConfiguration.value;
  if (config?.establishment) {
    const cartPageUrl = new URL(window.location.href);
    cartPageUrl.searchParams.set('cart', 'true');
    window.open(cartPageUrl.toString(), '_blank', 'noopener,noreferrer');
  }
  else if (config?.cartUrl) {
    window.open(config.cartUrl, '_blank', 'noopener,noreferrer');
  }
}

function handleContinueSearching() {
  handleCartModalClose();

  const config = globalConfiguration.value;
  if (config?.homeUrl) {
    window.location.href = config.homeUrl;
  }
}
</script>

<template>
  <div class="sessions-container">
    <div class="sessions-header">
      <div class="header-top">
        <h3 class="option-title">
          {{ optionTitle }}
        </h3>
        <p v-if="formattedDuration" class="option-duration">
          {{ formattedDuration }}
        </p>
      </div>

      <div v-if="hasNoAvailableSessions && !isLoadingAvailabilities" class="no-availabilities-badge" role="alert">
        <svg class="warning-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          />
        </svg>
        <span class="warning-text">
          {{ t('booking.noAvailabilitiesChangeDate') }}
        </span>
      </div>

      <div v-if="!hasNoAvailableSessions || isLoadingAvailabilities" class="header-separator" />
      <p v-if="!hasNoAvailableSessions || isLoadingAvailabilities" class="selected-date">
        <template v-if="t('booking.datePrefix')">
          {{ t('booking.datePrefix') }}
        </template>
        {{ formattedDate }}
      </p>
    </div>

    <template v-if="isLoadingAvailabilities">
      <div class="sessions-grid">
        <div v-for="i in SESSIONS_PAGE_SIZE" :key="i" class="session-skeleton" />
      </div>
    </template>

    <template v-else-if="!hasNoAvailableSessions">
      <div class="sessions-grid">
        <SessionCard
          v-for="session in visibleSessions"
          :key="session.id"
          :session="session"
          :is-selected="isSessionSelected(session)"
          :price="getSessionPrice(session)"
          :timezone="context.bookingWidget.productTimezone.value"
          @select="selectSession"
        />
      </div>

      <button
        v-if="hasMoreSessions"
        class="show-more-button"
        @click="showMoreSessions"
      >
        {{ t('booking.showMoreTimes') }}
      </button>

      <template v-if="hoursToBook !== null">
        <div class="footer-separator" />
        <div class="hours-to-book-badge" role="status">
          <svg class="info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
            <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <span class="hours-text">
            {{ t('booking.hoursToBook', { hours: hoursToBook }, hoursToBook) }}
          </span>
        </div>
      </template>

      <template v-if="context.bookingWidget.selectedSession.value !== null">
        <div v-if="hoursToBook === null" class="footer-separator" />

        <PricingBreakdown
          :currency="pricingCurrency"
          :currency-precision="pricingCurrencyPrecision"
          :units="pricingUnits"
        />

        <div class="action-buttons">
          <button class="add-to-cart-button" :disabled="isAddingToCart" @click="handleAddToCartClick">
            <span v-if="!isAddingToCart">
              {{ t('cta.add_to_cart') }}
            </span>
            <span v-else>
              <Loader2 :size="25" class="yb-spinner" aria-hidden="true" />
            </span>
          </button>
        </div>

        <div v-if="addToCartError" class="add-to-cart-error" role="alert">
          <svg class="warning-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>
          <span>{{ addToCartError }}</span>
        </div>
      </template>
    </template>

    <CartConfirmationModal
      :is-open="isCartModalOpen"
      @close="handleCartModalClose"
      @checkout="handleCheckout"
      @continue-searching="handleContinueSearching"
    />
  </div>
</template>

<style scoped lang="css">
.sessions-container {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
  padding: var(--yb-spacing-base);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: 8px;
  background-color: var(--yb-color-white);
}

.sessions-header {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}

.header-top {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-xs);
}

.header-separator {
  width: 100%;
  height: 1px;
  background-color: var(--yb-color-mediumgray);
}

.option-title {
  font-size: var(--yb-text-xl);
  font-weight: var(--yb-text-semi-bold);
  margin: 0;
  color: var(--yb-color-black);
}

.option-duration {
  font-size: var(--yb-text-sm);
  margin: 0;
}

.selected-date {
  font-size: var(--yb-text-base);
  font-weight: 500;
  margin: 0;
  color: var(--yb-color-black);
}

.sessions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--yb-spacing-sm);
}

@media (min-width: 768px) {
  .sessions-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.show-more-button {
  background: none;
  border: none;
  color: var(--yb-color-primary);
  font-size: var(--yb-text-base);
  font-weight: 500;
  cursor: pointer;
  padding: var(--yb-spacing-sm);
  text-align: center;
}

.show-more-button:hover {
  text-decoration: underline;
}

.footer-separator {
  width: 100%;
  height: 1px;
  background-color: var(--yb-color-mediumgray);
  margin-top: var(--yb-spacing-xs);
}

.hours-to-book-badge {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  border: 1.5px solid #f59e0b;
  border-radius: 8px;
  margin-top: var(--yb-spacing-xs);
}

.info-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #f59e0b;
}

.hours-text {
  font-size: var(--yb-text-base);
  font-weight: 500;
  color: #f59e0b;
}

.no-availabilities-badge {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: #fef3e8;
  border-radius: 8px;
  text-wrap: auto;
}

.warning-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #f59e0b;
}

.warning-text {
  font-size: var(--yb-text-base);
  font-weight: 500;
  color: #f59e0b;
}

.session-skeleton {
  min-height: 74px;
  max-height: 74px;
  border-radius: 8px;
  background-color: var(--yb-color-lightgray);
  animation: pulse 2s ease-in-out infinite;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.add-to-cart-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 2px solid var(--yb-color-black);
  border-radius: 8px;
  height: 56px;
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.add-to-cart-button:hover:not(:disabled) {
  background-color: #f5f5f5;
}

.add-to-cart-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-to-cart-error {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: color-mix(in srgb, var(--yb-color-error) 10%, white);
  border-radius: 8px;
  color: var(--yb-color-error);
}

.add-to-cart-error .warning-icon {
  color: #dc2626;
}

.add-to-cart-error span {
  font-size: var(--yb-text-base);
  font-weight: 500;
}

.yb-spinner {
  vertical-align: middle;
}

@keyframes pulse {
  50% {
    opacity: 0.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .session-skeleton {
    animation: none;
  }

  .add-to-cart-button {
    transition: none;
  }
}
</style>
